// Bidirectional adapter between the Hermes recruiting panel state and the
// core-api UpdateRecruitingDto shape. Field-by-field mapping, no side effects.

// Nullish fallback. Webpack 4 in this repo does not parse `??`.
function nullish(value, fallback) {
  return value === null || value === undefined ? fallback : value;
}

// Build the public wizard URL for a given community. Uses RECRUITING_BASEURL
// so the value stored in Mongo matches the current environment (dev deploy
// stores the dev URL, prod deploy stores the prod URL). Backend @IsUrl()
// rejects localhost, so local dev falls back to the prod domain — that
// stored value is never used by the local wizard itself.
function buildWizardURL(communityId) {
  var base = process.env.RECRUITING_BASEURL || 'https://opportunities.protopia.co';
  if (base.indexOf('http://localhost') === 0) {
    base = 'https://opportunities.protopia.co';
  }
  return base + '/submit/' + communityId;
}

// core-api response  →  local panel state
export function apiToHermes(apiData) {
  const data = apiData || {};
  const approvals = data.approvals || {};
  const rules = data.rules || {};
  const alerts = data.alerts || {};

  return {
    moduleEnabled: nullish(data.enabled, false),
    jobPostingUrl: nullish(data.wizardURL, ''),
    // "Learn More" link for the recruiting wizard footer. Independent
    // from community.content.landingPageURL
    learnMoreURL: nullish(data.learnMoreURL, ''),

    administrators: {
      approvalNewJob: nullish(alerts.approvalNewJob, ''),
      alertJob: nullish(alerts.alertJob, ''),
      alertFeedback: nullish(alerts.alertFeedback, ''),
      alertApplication: nullish(alerts.alertApplication, ''),
      alertSecuredJob: nullish(alerts.alertSecuredJob, ''),
      alertUnsubscribe: nullish(alerts.alertUnsubscribe, ''),
      alertProfileUpdate: nullish(alerts.alertProfileUpdate, ''),
    },

    notifications: {
      requireApprovals: nullish(approvals.requireApprovals, true),
      approvalEmails: (nullish(approvals.approverEmails, [])).join(', '),
      jobReminder: nullish(approvals.jobReminder, false) === true
        || nullish(approvals.jobReminder, 'false') === 'true',
    },

    matching: {
      minAdvisorsPerJob: nullish(rules.minimumAdvisors, 25),
      maxAdvisorsPerJob: nullish(rules.maximumAdvisors, 100),
      refillEnabled: nullish(rules.refill, false),
      interestClicks: nullish(rules.interestClicks, 5),
      daysToRefill: nullish(rules.daysToRefill, 3),
      minAdvisorsForRefill: nullish(rules.minimumForRefill, 5),
    },
  };
}

// Local panel state  →  core-api PATCH payload
export function hermesToApi(config, community) {
  const emails = (config.notifications.approvalEmails || '')
    .split(',')
    .map(function (s) { return s.trim(); })
    .filter(function (s) { return !!s; });

  const customSettings = (community && community.customSettings) || {};

  // Trim + only send learnMoreURL when non-empty.
  const trimmedLearnMore = (config.learnMoreURL || '').trim();

  return {
    enabled: config.moduleEnabled,
    wizardURL: buildWizardURL(community._id),
    ...(trimmedLearnMore ? { learnMoreURL: trimmedLearnMore } : {}),

    page: {
      active: config.moduleEnabled,
      title: nullish(community.name, ''),
      slug: nullish(community.code, ''),
      primaryColor: nullish(customSettings.primaryColor, '#512888'),
    },

    approvals: {
      requireApprovals: config.notifications.requireApprovals,
      approverEmails: emails,
      // DTO expects a string here; cast the boolean to keep validation happy.
      jobReminder: String(!!config.notifications.jobReminder),
    },

    rules: {
      minimumAdvisors: config.matching.minAdvisorsPerJob,
      maximumAdvisors: config.matching.maxAdvisorsPerJob,
      refill: config.matching.refillEnabled,
      interestClicks: config.matching.interestClicks,
      daysToRefill: config.matching.daysToRefill,
      minimumForRefill: config.matching.minAdvisorsForRefill,
    },

    alerts: {
      approvalNewJob: config.administrators.approvalNewJob,
      alertJob: config.administrators.alertJob,
      alertFeedback: config.administrators.alertFeedback,
      alertApplication: config.administrators.alertApplication,
      alertSecuredJob: config.administrators.alertSecuredJob,
      alertUnsubscribe: config.administrators.alertUnsubscribe,
      alertProfileUpdate: config.administrators.alertProfileUpdate,
    },
  };
}



// WEBPACK FOOTER //
// ./src/services/recruitingConfigAdapter.js